import { useReducer, useCallback } from 'react';
import isEqualWith from 'lodash.isequalwith';
import delve from 'dlv';
import merge from 'lodash.merge';

const OFFSET = 'OFFSET';
const RESET_OFFSET = 'RESET_OFFSET';
const ENTITIES = 'ENTITIES';
const INVALIDATE = 'INVALIDATE';
const VALIDATE = 'VALIDATE';
const SET_STATE = 'SET_STATE';

export default useOffset;

type TSetEntities = (value: any, ignoreComparator?: boolean) => void;

type TFunction = () => void;

interface IUseOffset {
  offsetState: IOffsetState;
  setOffset: (newLimit?: number) => void;
  setEntities: TSetEntities;
  setInvalidate: TFunction;
  setValidate: TFunction;
  resetOffset: TFunction;
  setState: any;
}

interface IOffsetConfiguration {
  limit: number;
  increment: number;
  entitiesKey: string;
}

interface IOffsetState {
  limit: number;
  increment: number;
  offset: number;
  value: any;
}

interface IInternalOffsetState extends IOffsetState {
  invalidate: boolean;
}

/*
 * This hook is a strategy to manage the limit and offset parameters that
 * get send to the API as well as concatinating and invalidating the subsequent
 * responses in state. It is used in the tables as they allow pagination.
 *
 * @param {Number} args.limit how many entities should be returned from the API
 * @param {Number} args.increment how many indexes should the API skip at trigger
 * @param {Array} args.entitiesKey key(s) to get the array of entities inside `entityFetch.value`
 *
 * @returns {Object} { { limit, offset, value }, setOffset, setEntities, setInvalidate }
 *
 * Example
 * ```
 * const { offsetState, setOffset, setEntities, setInvalidate } = useOffset({
 *   limit: Number,
 *   increment: Number,
 *   entitiesKey: Array,
 * });
 * ```
 *
 * Event emitters
 *
 * `setOffset()`
 * Increments the offset which triggers a new API call externally.
 * Once the new data is fulfilled, `setEntities` should be called with the response value.
 *
 * `setEntities(value, ignoreComparator)`
 * @param {Object} value the response value of the API call.
 * @param {Function} ignoreComparator optional function to specify when current and new entities
 *                   should be considered the same. Falls back to comparing everything.
 *                   Also performance boost!
 *
 * Is invalidate true? Override the value with the new data.
 * Is state.value null? Set the value with the new data.
 * Is the new data the same as in state? Do nothing.
 * Otherwise: concat the current and new entities.
 *
 * `setInvalidate()`
 * Should be called on interactions that trigger an API call.
 * Sets `invalidate: true` which will be picked up by `setEntities`
 * once the new data comes in. This is to keep data in state until new data arrives.
 *
 */
function useOffset(config: IOffsetConfiguration): IUseOffset {
  const { limit, increment, entitiesKey } = config;

  const initialState: IInternalOffsetState = {
    limit,
    increment,
    offset: 0,
    value: null,
    invalidate: false,
  };
  const [state, dispatch] = useReducer<
    (state: IInternalOffsetState, action: any) => IInternalOffsetState
  >(reducer, initialState);
  // invalidate is only used internally and does not need te be returned.
  const { invalidate, ...offsetState } = state;

  const setOffset = useCallback(
    (newLimit) =>
      dispatch({
        type: OFFSET,
        increment,
        initialLimit: typeof newLimit === 'number' ? increment : limit,
      }),
    [increment, limit]
  );

  const setEntities = useCallback(
    (value, ignoreComparator) =>
      dispatch({
        type: ENTITIES,
        value,
        invalidate,
        entitiesKey,
        ignoreComparator,
      }),
    [entitiesKey, invalidate]
  );

  const resetOffset = useCallback(() => {
    return dispatch({ type: RESET_OFFSET, initialLimit: limit });
  }, [limit]);

  const setInvalidate = useCallback(() => dispatch({ type: INVALIDATE }), []);
  const setValidate = useCallback(() => dispatch({ type: VALIDATE }), []);
  const setState = useCallback(
    ({ limit, offset }: { limit: number; offset: number }): void =>
      dispatch({ type: SET_STATE, state: { limit, offset } }),
    []
  );

  return {
    offsetState,
    setOffset,
    setEntities,
    setInvalidate,
    resetOffset,
    setValidate,
    setState,
  };
}

function setEntities(
  currentEntities: any[],
  newEntities: any[]
): (acc: any[], key: string, idx: number) => any {
  return (acc: any[], key: string, idx: number): any => {
    if (idx === 0) {
      return { [key]: currentEntities.concat(newEntities) };
    }
    return { [key]: { ...acc } };
  };
}

function reducer(
  state: IInternalOffsetState,
  action: any
): IInternalOffsetState {
  switch (action.type) {
    case INVALIDATE: {
      // When the user has clicked `Show [offset] more` N times, remember that
      // so the new data has the same number of entities.
      const limit = state.offset > 0 ? state.offset + state.limit : state.limit;
      return { ...state, offset: 0, limit, invalidate: true };
    }
    case VALIDATE: {
      return { ...state, invalidate: false };
    }
    case OFFSET:
      return {
        ...state,
        offset:
          state.offset === 0 && state.limit !== action.increment
            ? state.limit
            : state.offset + action.increment,
        // reset limit otherwise too many entities can be requested (see above).
        limit: action.initialLimit,
      };
    case RESET_OFFSET:
      return {
        ...state,
        offset: 0,
        limit: action.initialLimit,
      };
    case SET_STATE:
      return { ...state, ...action.state };

    case ENTITIES: {
      const key = action.entitiesKey;

      if (action.invalidate) {
        return { ...state, value: action.value, invalidate: false };
      }
      if (!state.value) {
        return { ...state, value: action.value };
      }
      if (!delve(state.value, key, false)) {
        return state;
      }

      const currentEntities = delve(state.value, key);
      const newEntities = delve(action.value, key);

      if (isEqualWith(currentEntities, newEntities, action.ignoreComparator)) {
        return state;
      }

      // Sometimes the entities array isn't directly accessible
      // by a key but is nested instead. In order to maintain the original
      // structure, we need to reduce an object in reversed order so
      // we can start with the deepest key, which is where we need to
      // concat the entities, and then wrap with an object for every
      // subsequent key.
      //
      // Example: a.b.c = entities
      // { c: [entities] }
      // { b: { c: [entities] } }
      // { a: { b: { c: [entities] } } }
      const nestedEntities = key
        .split('.')
        .reverse()
        .reduce(setEntities(currentEntities, newEntities), {});

      return {
        ...state,
        value: merge(state.value, nestedEntities),
      };
    }
    default:
      throw new Error('useOffset reducer was called without an action.type');
  }
}
